import { useEffect, useState } from 'react';
import { ThumbsUp, ThumbsDown, Loader2, Trash2 } from 'lucide-react';
import { useWorkoutStore } from '../stores/workoutStore';
import { useFeedbackStore } from '../stores/feedbackStore';
import { cn } from '../lib/utils';
import type { FeedbackRating } from '../lib/api';

interface DayFeedbackProps {
  date: string;
}

/**
 * Thumbs-up / thumbs-down + free-text notes for a single workout day.
 * Saves to the ai_generation_feedback table via /admin/validation/feedback.
 *
 * One rating per admin per user per date — re-submitting updates the
 * existing row. Server-side snapshots the day's workout + the user's
 * profile at save time so the row remains a useful post-training sample.
 */
export function DayFeedback({ date }: DayFeedbackProps) {
  const currentUser = useWorkoutStore((s) => s.currentUser);
  const userId = currentUser?.id;

  const { byDate, pendingByKey, loadForUser, submitDay, remove } = useFeedbackStore();
  const existing = byDate[date];
  const isPending = !!pendingByKey[`day:${date}`];

  // Ensure feedback for this user has been hydrated. The store dedupes
  // across the many DayCard mounts that fire on a single season load.
  useEffect(() => {
    if (userId) void loadForUser(userId);
  }, [userId, loadForUser]);

  const [expanded, setExpanded] = useState(false);
  const [draftRating, setDraftRating] = useState<FeedbackRating | null>(null);
  const [draftNotes, setDraftNotes] = useState('');

  // Hydrate draft from existing row whenever it changes (e.g. after
  // a sibling submit invalidates).
  useEffect(() => {
    setDraftRating(existing?.rating ?? null);
    setDraftNotes(existing?.notes ?? '');
  }, [existing?.id, existing?.rating, existing?.notes]);

  if (!userId) return null;

  const savedRating = existing?.rating ?? null;
  const hasUnsavedChanges =
    draftRating !== null &&
    (draftRating !== savedRating || (draftNotes ?? '') !== (existing?.notes ?? ''));

  async function handleSubmit() {
    if (!userId || !draftRating) return;
    const row = await submitDay({
      userId,
      workoutDate: date,
      rating: draftRating,
      notes: draftNotes.trim() || undefined,
    });
    if (row) setExpanded(false);
  }

  async function handleRemove() {
    if (!existing) return;
    await remove(existing.id);
    setDraftRating(null);
    setDraftNotes('');
    setExpanded(false);
  }

  function pickRating(next: FeedbackRating) {
    setDraftRating(next);
    setExpanded(true);
  }

  return (
    <div className="mt-3 border-t border-gray-100 pt-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <button
            onClick={() => pickRating('up')}
            disabled={isPending}
            title="Good workout"
            className={cn(
              'flex h-7 w-7 items-center justify-center rounded-full border transition',
              draftRating === 'up'
                ? 'border-green-500 bg-green-50 text-green-700'
                : 'border-gray-200 text-gray-400 hover:border-gray-300 hover:text-gray-600',
            )}
          >
            <ThumbsUp className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => pickRating('down')}
            disabled={isPending}
            title="Bad workout"
            className={cn(
              'flex h-7 w-7 items-center justify-center rounded-full border transition',
              draftRating === 'down'
                ? 'border-red-500 bg-red-50 text-red-700'
                : 'border-gray-200 text-gray-400 hover:border-gray-300 hover:text-gray-600',
            )}
          >
            <ThumbsDown className="h-3.5 w-3.5" />
          </button>
          {savedRating && !expanded && (
            <span className="text-xs text-gray-500">
              {savedRating === 'up' ? '👍 saved' : '👎 saved'}
              {existing?.notes ? ' · with notes' : ''}
            </span>
          )}
        </div>
        {(savedRating || draftRating) && (
          <button
            onClick={() => setExpanded((v) => !v)}
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            {expanded ? 'Hide' : existing?.notes ? 'View notes' : 'Add notes'}
          </button>
        )}
      </div>

      {expanded && (
        <div className="mt-2 space-y-2">
          <textarea
            rows={3}
            placeholder={
              draftRating === 'up'
                ? 'Why is this programming correct? (e.g. good exercise pairing, right intensity for week 3…)'
                : draftRating === 'down'
                  ? 'What is wrong with this programming? (e.g. wrong rep scheme, missing primary lift, intensity too high…)'
                  : 'Pick 👍 or 👎 first, then add reasoning.'
            }
            value={draftNotes}
            onChange={(e) => setDraftNotes(e.target.value)}
            disabled={isPending || !draftRating}
            className="w-full resize-none rounded-md border border-gray-300 bg-white p-2 text-xs text-gray-700 focus:border-primary focus:outline-hidden focus:ring-1 focus:ring-primary disabled:bg-gray-50"
          />
          <div className="flex items-center justify-between gap-2">
            <div>
              {existing && (
                <button
                  onClick={handleRemove}
                  disabled={isPending}
                  className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700"
                  title="Delete this feedback row"
                >
                  <Trash2 className="h-3 w-3" />
                  Delete
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setExpanded(false);
                  setDraftRating(existing?.rating ?? null);
                  setDraftNotes(existing?.notes ?? '');
                }}
                disabled={isPending}
                className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!draftRating || !hasUnsavedChanges || isPending}
                className={cn(
                  'flex items-center gap-1 rounded-md px-3 py-1 text-xs font-medium transition',
                  draftRating && hasUnsavedChanges && !isPending
                    ? 'bg-primary text-white hover:bg-primary/90'
                    : 'bg-gray-100 text-gray-400',
                )}
              >
                {isPending && <Loader2 className="h-3 w-3 animate-spin" />}
                {existing ? 'Update' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
